import { useMemo } from 'react';
import type { PriceQuote } from '@ducat-unit/core';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { getFrontendNetwork } from '@/config/deployment';
import { DEFAULT_PRICE_QUOTE } from '@/constants';
import type { QuantaHeaders } from '@/hooks/api/quanta/auth';
import type { Fee } from '@/utils/fee.utils';

import { deriveConfig } from './helpers';

export type UserNetworkSettings = {
  guardianUrl?: string;
  customGuardianUrl?: string | undefined;
  customGuardianPubkey?: string | undefined;
  guardMemberPubkey?: string | undefined;
};
export interface AppState {
  customFees: Fee[];
  selectedFee?: Fee;
  isActionActive: boolean;
  resetDrawers: boolean;
  walletErrorMessage: string;
  quantaHeaders: QuantaHeaders | undefined;
  priceQuote: PriceQuote;
  /** Bumped by Notification Center "Continue" to open SwapDrawer for a pending swap. */
  openSwapDrawerRequestId: number;
  /** Bumped by Notification Center "Skip" to reset an in-flight SwapDrawer second leg. */
  skipPendingSwapRequestId: number;

  // Network settings user selection:
  userNetworkSettings: undefined | UserNetworkSettings;

  // setters
  setUserNetworkSettings: (userNetworkSettings: UserNetworkSettings) => void;
  setCustomFees: (fees: Fee[]) => void;
  addCustomFee: (fee: Fee) => void;
  removeCustomFee: (rate: number, fallback: Fee) => void;
  setSelectedFee: (fee?: Fee) => void;
  setIsActionActive: (v: boolean) => void;
  setWalletErrorMessage: (msg: string) => void;
  setQuantaHeaders: (headers: QuantaHeaders | undefined) => void;
  setResetDrawers: (v: boolean | undefined) => void;
  setPriceQuote: (v: PriceQuote) => void;
  requestOpenSwapDrawer: () => void;
  requestSkipPendingSwap: () => void;

  // To track last vault action:
  txid?: string;
  pendingTxCompleted: boolean;
  isVaultCreationFlowInProgress: boolean;
  setTxid: (txid: string | undefined) => void;
  setPendingTxCompeted: (v: boolean) => void;
  setVaultCreationFlowInProgress: (v: boolean) => void;
  updatePendingTx: (txid: string | undefined, completed: boolean) => void;
  resetActionTx: () => void;
}

export const APP_STORE_PERSIST_VERSION = 6;

export function projectPersistedAppState(state: AppState) {
  return {
    selectedFee: state.selectedFee,
    userNetworkSettings: { ...state.userNetworkSettings, guardianUrl: '', guardMemberPubkey: '' },
    customFees: state.customFees,
    quantaHeaders: state.quantaHeaders,
  };
}

export function migratePersistedAppState(persistedState: unknown): unknown {
  if (!persistedState || typeof persistedState !== 'object' || Array.isArray(persistedState)) return persistedState;
  const restoredState = { ...(persistedState as Record<string, unknown>) };
  // Drop retired selectors while preserving unrelated preferences and signed headers.
  delete restoredState.selectedNetwork;
  delete restoredState.overviewCombineWithV2;
  return restoredState;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      customFees: [],
      selectedFee: undefined,
      isActionActive: false,
      resetDrawers: false,
      walletErrorMessage: '',
      quantaHeaders: undefined,
      txid: undefined,
      pendingTxCompleted: false,
      isVaultCreationFlowInProgress: false,
      userNetworkSettings: undefined,
      openSwapDrawerRequestId: 0,
      skipPendingSwapRequestId: 0,

      priceQuote: DEFAULT_PRICE_QUOTE,

      setUserNetworkSettings: (userNetworkSettings) => {
        set({ userNetworkSettings });
      },
      setCustomFees: (customFees) => set({ customFees }),
      addCustomFee: (fee: Fee) =>
        set((state) => ({
          customFees: state.customFees.some((saved) => saved.fee === fee.fee) ? state.customFees : [...state.customFees, fee],
        })),
      removeCustomFee: (rate, fallback) =>
        set((state) => ({
          customFees: state.customFees.filter((saved) => saved.fee !== rate),
          selectedFee: state.selectedFee?.label === 'Custom' && state.selectedFee.fee === rate ? fallback : state.selectedFee,
        })),
      setSelectedFee: (selectedFee) => set({ selectedFee }),
      setPriceQuote: (priceQuote) => set({ priceQuote }),
      setIsActionActive: (isActionActive) => set({ isActionActive }),
      setResetDrawers: (resetDrawers) => set({ resetDrawers }),
      setWalletErrorMessage: (walletErrorMessage) => set({ walletErrorMessage }),
      setQuantaHeaders: (quantaHeaders) => set({ quantaHeaders }),
      setTxid: (txid) => set({ txid }),
      setPendingTxCompeted: (pendingTxCompleted) => set({ pendingTxCompleted }),
      updatePendingTx: (txid, pendingTxCompleted) => set({ txid, pendingTxCompleted }),
      resetActionTx: () => set({ txid: undefined, pendingTxCompleted: false, isVaultCreationFlowInProgress: false }),
      setVaultCreationFlowInProgress: (isVaultCreationFlowInProgress) => set({ isVaultCreationFlowInProgress }),
      requestOpenSwapDrawer: () => set((state) => ({ openSwapDrawerRequestId: state.openSwapDrawerRequestId + 1 })),
      requestSkipPendingSwap: () => set((state) => ({ skipPendingSwapRequestId: state.skipPendingSwapRequestId + 1 })),
    }),
    {
      name: 'app-store', // stored in localStorage
      version: APP_STORE_PERSIST_VERSION,
      migrate: migratePersistedAppState,
      partialize: projectPersistedAppState,

      onRehydrateStorage: () => (state) => {
        if (!state) return;

        if (state.userNetworkSettings) {
          state.userNetworkSettings = { ...state.userNetworkSettings, guardianUrl: '', guardMemberPubkey: '' };
        }
      },
    },
  ),
);

// Combine network config and user settings selection:
export const useConfig = () => {
  const userNetworkSettings = useAppStore((state) => state.userNetworkSettings);
  const frontendNetwork = getFrontendNetwork();

  const configSettings = useMemo(() => deriveConfig(frontendNetwork, userNetworkSettings), [frontendNetwork, userNetworkSettings]);

  return configSettings;
};

export const useNetwork = () => {
  return getFrontendNetwork();
};
